import { useState, useRef, useCallback, useEffect } from "react";

export type RecordingState = "inactive" | "recording" | "paused";

export type MediaRecordingOptions = {
  audio?: boolean;
  video?: boolean;
  mimeType?: string;
  audioBitsPerSecond?: number;
  videoBitsPerSecond?: number;
  /** RMS amplitude threshold (0–1) above which a chunk is considered speech. Defaults to 0.01. */
  speechThreshold?: number;
  /**
   * When set, automatically stops recording after this many milliseconds of
   * continuous silence (RMS below speechThreshold). Disabled when omitted.
   */
  silenceTimeoutMs?: number;
  /** How often (ms) to sample RMS for silence detection. Defaults to 200. */
  silencePollingIntervalMs?: number;
};

export type UseMediaRecordingParams = {
  /** Called with each audio chunk that contains detected speech (RMS above threshold). */
  onSpeechChunk?: (chunk: Blob) => void;
  /**
   * Called once when the user explicitly stops recording, with a single Blob
   * containing all speech-detected chunks from the session.
   * Not called when no speech was detected.
   */
  onSpeechInterval?: (audio: Blob) => void;
};

type UseMediaRecordingReturn = {
  recordingState: RecordingState;
  isRecording: boolean;
  isPaused: boolean;
  recordedBlob: Blob | null;
  recordedUrl: string | null;
  durationMs: number;
  error: string | null;
  start: (options?: MediaRecordingOptions) => Promise<void>;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  reset: () => void;
};

const DEFAULT_OPTIONS: MediaRecordingOptions = {
  audio: true,
  video: false,
};

const DEFAULT_SPEECH_THRESHOLD = 0.01;

const computeRMS = (analyser: AnalyserNode): number => {
  const buffer = new Float32Array(analyser.fftSize);
  analyser.getFloatTimeDomainData(buffer);
  const sumOfSquares = buffer.reduce((sum, v) => sum + v * v, 0);
  return Math.sqrt(sumOfSquares / buffer.length);
};

export const useMediaRecording = ({
  onSpeechChunk,
  onSpeechInterval,
}: UseMediaRecordingParams = {}): UseMediaRecordingReturn => {
  const [recordingState, setRecordingState] = useState<RecordingState>("inactive");
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [durationMs, setDurationMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number | null>(null);
  const durationTimerRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const speechIntervalChunksRef = useRef<Blob[]>([]);
  const silencePollingRef = useRef<number | null>(null);
  const lastSpeechTimeRef = useRef<number>(Date.now());
  const mimeTypeRef = useRef<string>("audio/webm");
  // Always keep the very first chunk — it contains the codec init/header data
  // required to build a valid audio file from subsequent speech-only chunks.
  const initChunkRef = useRef<Blob | null>(null);
  // Always hold the latest callbacks without causing effect re-runs
  const onSpeechChunkRef = useRef<((chunk: Blob) => void) | undefined>(onSpeechChunk);
  const onSpeechIntervalRef = useRef<((audio: Blob) => void) | undefined>(onSpeechInterval);

  useEffect(() => {
    onSpeechChunkRef.current = onSpeechChunk;
  }, [onSpeechChunk]);

  useEffect(() => {
    onSpeechIntervalRef.current = onSpeechInterval;
  }, [onSpeechInterval]);

  const clearSilencePolling = () => {
    if (silencePollingRef.current !== null) {
      window.clearInterval(silencePollingRef.current);
      silencePollingRef.current = null;
    }
  };

  const stopDurationTimer = () => {
    if (durationTimerRef.current !== null) {
      window.clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  };

  const startDurationTimer = () => {
    startTimeRef.current = Date.now() - durationMs;
    durationTimerRef.current = window.setInterval(() => {
      if (startTimeRef.current !== null) {
        setDurationMs(Date.now() - startTimeRef.current);
      }
    }, 100);
  };

  const flushSpeechInterval = () => {
    if (speechIntervalChunksRef.current.length === 0) return;
    // Prepend the init chunk (codec headers) so the server receives a valid
    // audio file even when only mid-stream speech chunks were collected.
    const chunks =
      initChunkRef.current && speechIntervalChunksRef.current[0] !== initChunkRef.current
        ? [initChunkRef.current, ...speechIntervalChunksRef.current]
        : speechIntervalChunksRef.current;
    const audio = new Blob(chunks, { type: mimeTypeRef.current });
    speechIntervalChunksRef.current = [];
    onSpeechIntervalRef.current?.(audio);
  };

  const releaseStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const releaseAudioContext = () => {
    analyserRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => { });
      audioContextRef.current = null;
    }
  };

  const revokeUrl = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
  };

  const start = useCallback(async (options: MediaRecordingOptions = DEFAULT_OPTIONS) => {
    setError(null);

    if (recordingState !== "inactive") {
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: options.audio ?? true,
        video: options.video ?? false,
      });

      streamRef.current = stream;
      chunksRef.current = [];
      revokeUrl();
      setRecordedBlob(null);
      setRecordedUrl(null);
      setDurationMs(0);

      // Set up Web Audio analyser for speech detection (audio-only streams)
      if (options.audio !== false) {
        try {
          const audioCtx = new AudioContext();
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 512;
          audioCtx.createMediaStreamSource(stream).connect(analyser);
          audioContextRef.current = audioCtx;
          analyserRef.current = analyser;
        } catch {
          // Non-fatal — speech detection will be skipped
        }
      }

      const speechThreshold = options.speechThreshold ?? DEFAULT_SPEECH_THRESHOLD;

      if (options.silenceTimeoutMs !== undefined && analyserRef.current) {
        const silenceTimeoutMs = options.silenceTimeoutMs;
        const pollingMs = options.silencePollingIntervalMs ?? 200;
        lastSpeechTimeRef.current = Date.now();

        silencePollingRef.current = window.setInterval(() => {
          const analyser = analyserRef.current;
          const recorder = mediaRecorderRef.current;
          if (!analyser || !recorder || recorder.state !== "recording") return;

          const rms = computeRMS(analyser);
          if (rms > speechThreshold) {
            lastSpeechTimeRef.current = Date.now();
          } else if (Date.now() - lastSpeechTimeRef.current >= silenceTimeoutMs) {
            clearSilencePolling();
            recorder.stop();
            stopDurationTimer();
          }
        }, pollingMs);
      }

      speechIntervalChunksRef.current = [];
      const recorderOptions: MediaRecorderOptions = {};
      if (options.mimeType && MediaRecorder.isTypeSupported(options.mimeType)) {
        recorderOptions.mimeType = options.mimeType;
      }
      if (options.audioBitsPerSecond) {
        recorderOptions.audioBitsPerSecond = options.audioBitsPerSecond;
      }
      if (options.videoBitsPerSecond) {
        recorderOptions.videoBitsPerSecond = options.videoBitsPerSecond;
      }

      const recorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = recorder;
      // Use the recorder's actual mimeType (set after construction) so the
      // Blob type matches the real codec (e.g. audio/webm;codecs=opus on Chrome,
      // audio/mp4 on Safari) rather than a generic fallback.
      mimeTypeRef.current = recorder.mimeType || recorderOptions.mimeType || "audio/webm";
      initChunkRef.current = null;

      recorder.ondataavailable = (event) => {
        if (event.data.size === 0) return;

        // First chunk always contains codec init data — save it unconditionally.
        if (!initChunkRef.current) {
          initChunkRef.current = event.data;
        }

        chunksRef.current.push(event.data);

        if (analyserRef.current) {
          const rms = computeRMS(analyserRef.current);
          const isSpeech = rms > speechThreshold;

          if (true || isSpeech) {
            onSpeechChunkRef.current?.(event.data);
            speechIntervalChunksRef.current.push(event.data);
          }
        }
      };

      // Flush all accumulated speech chunks once when the user explicitly stops.
      recorder.onstop = () => {
        clearSilencePolling();
        flushSpeechInterval();
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setRecordedUrl(url);
        setRecordingState("inactive");
        stopDurationTimer();
        releaseAudioContext();
        releaseStream();
      };

      recorder.onerror = () => {
        clearSilencePolling();
        speechIntervalChunksRef.current = [];
        setError("An error occurred during recording.");
        setRecordingState("inactive");
        stopDurationTimer();
        releaseAudioContext();
        releaseStream();
      };

      recorder.start(3000);
      setRecordingState("recording");
      startDurationTimer();
    } catch (err) {
      const e = err as { name?: string; message?: string };
      if (e.name === "NotAllowedError" || e.message?.includes("Permission denied")) {
        setError("Microphone/camera permission was denied.");
      } else if (e.name === "NotFoundError") {
        setError("No microphone or camera found.");
      } else {
        setError(e.message ?? "Failed to start recording.");
      }
      releaseAudioContext();
      releaseStream();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingState]);

  const stop = useCallback(() => {
    clearSilencePolling();
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") return;
    recorder.stop();
    stopDurationTimer();
  }, []);

  const pause = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    recorder.pause();
    stopDurationTimer();
    setRecordingState("paused");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resume = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "paused") return;
    recorder.resume();
    startDurationTimer();
    setRecordingState("recording");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationMs]);

  const reset = useCallback(() => {
    clearSilencePolling();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    stopDurationTimer();
    speechIntervalChunksRef.current = [];
    releaseAudioContext();
    releaseStream();
    chunksRef.current = [];
    mediaRecorderRef.current = null;
    revokeUrl();
    setRecordedBlob(null);
    setRecordedUrl(null);
    setDurationMs(0);
    setRecordingState("inactive");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordedUrl]);

  return {
    recordingState,
    isRecording: recordingState === "recording",
    isPaused: recordingState === "paused",
    recordedBlob,
    recordedUrl,
    durationMs,
    error,
    start,
    stop,
    pause,
    resume,
    reset,
  };
};
